/**
 * vendorOutlets.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE reading of a vendor's outlets (branches) for every vertical that has
 * them: restaurants, pharmacies and brands.
 *
 * An outlet is a location of its own (address, coordinates, delivery radius,
 * hours) that sells its parent's catalogue. Every location (the parent's own
 * row and each outlet) becomes a location card here; the Brands and
 * Restaurants listings then show ONE card per vendor, opened on its best
 * location for the shopper (`onePerVendor`, at the bottom), and the
 * storefront's location picker lets them choose another
 * (`vendorLocationsOf`). The storefront is the parent's storefront opened
 * with `?outletId=`, and every cart line added there carries that id so the
 * coverage check, the fee quote, the delivery slots and the order itself all
 * resolve the same branch.
 *
 * The three listing pages used to each carry their own copy of the flatten
 * loop. The copies had already drifted (one inherited the parent's open state,
 * one did not), which is the reason this file exists.
 */

export const OUTLET_QUERY_PARAM = "outletId";

/** The outlet named by a storefront URL's query string, or null. */
export const outletIdFromSearch = (search) => {
  const value = new URLSearchParams(search || "").get(OUTLET_QUERY_PARAM);
  return value ? String(value) : null;
};

/** `path` with `?outletId=` appended when an outlet is selected. */
export const withOutletQuery = (path, outletId) => {
  if (!outletId) return path;
  const joiner = String(path).includes("?") ? "&" : "?";
  return `${path}${joiner}${OUTLET_QUERY_PARAM}=${encodeURIComponent(String(outletId))}`;
};

/** The outlets a shopper may order from: active and present. */
export const activeOutletsOf = (vendor) =>
  Array.isArray(vendor?.outlets)
    ? vendor.outlets.filter((outlet) => outlet && outlet.isActive !== false)
    : [];

/** The outlet with this id on this vendor, or null (including for a null id). */
export const findOutlet = (vendor, outletId) => {
  if (!outletId) return null;
  return (
    activeOutletsOf(vendor).find(
      (outlet) => String(outlet._id) === String(outletId),
    ) || null
  );
};

/** "<Outlet name>, <address>" for an identity card or an order summary. */
export const outletLabel = (outlet) =>
  [outlet?.name, outlet?.address]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");

/**
 * The outlet a cart is being ordered from, read off its lines. Every line
 * added from an outlet storefront carries the same id, so the first one found
 * is the cart's. Accepts the cart map or its values.
 */
export const cartOutletId = (lines) => {
  const list = Array.isArray(lines) ? lines : Object.values(lines || {});
  return list.find((line) => line?.outletId)?.outletId || null;
};

/**
 * The row an outlet contributes to a listing, built from its parent. Fields
 * every vertical's card reads; a page adds its own through `outletRow` below.
 *
 * `openStatus` is copied only when the outlet carries one (the server resolves
 * it from the outlet's hours, inheriting the parent's when it has none). A
 * listing whose outlets never had the field keeps whatever reading its own
 * `outletRow` supplies, so it does not start inheriting the parent's status.
 */
const baseOutletRow = (vendor, outlet) => ({
  _id: outlet._id,
  name: outlet.name,
  address: outlet.address,
  latitude: outlet.latitude,
  longitude: outlet.longitude,
  imageUrl: vendor.imageUrl,
  ownerDetails: vendor.ownerDetails,
  isActive: true,
  isOutlet: true,
  parentVendorId: vendor._id,
  parentVendorName: vendor.name,
  deliveryZoneRadius: outlet.deliveryZoneRadius || vendor.deliveryZoneRadius,
  owner: vendor.owner,
  ...(outlet.openStatus ? { openStatus: outlet.openStatus } : {}),
  offerSummary: vendor.offerSummary || null,
});

/**
 * Vendors and their active outlets as one flat list of locations, each a
 * card of its own: the parent first (`isOutlet: false, isMainBranch: true`),
 * then its outlets (`isOutlet: true`), in the order the API listed them.
 *
 * @param {Array}    vendors
 * @param {object}   [options]
 * @param {Function} [options.isListed]  vendor => boolean; a vendor it refuses
 *                                       is skipped along with its outlets
 * @param {Function} [options.outletRow] (vendor, outlet) => extra fields for
 *                                       that vertical's outlet card
 */
export const flattenVendorLocations = (
  vendors,
  { isListed = () => true, outletRow = null } = {},
) => {
  const locations = [];
  (Array.isArray(vendors) ? vendors : []).forEach((vendor) => {
    if (!vendor || vendor.isActive === false || !isListed(vendor)) return;
    locations.push({ ...vendor, isOutlet: false, isMainBranch: true });
    activeOutletsOf(vendor).forEach((outlet) => {
      locations.push({
        ...baseOutletRow(vendor, outlet),
        ...(outletRow ? outletRow(vendor, outlet) : {}),
      });
    });
  });
  return locations;
};

/**
 * The coverage-batch entry for a listed location: the parent's id, plus the
 * outlet's when the card is an outlet, so the verdict is measured from the
 * outlet's own door. `vendorId` is whatever id the vertical orders with.
 */
export const coverageEntryFor = (location, vendorId) =>
  location?.isOutlet
    ? { vendorId: String(vendorId), outletId: String(location._id) }
    : { vendorId: String(vendorId) };

/** The key a coverage cache entry sits under: the outlet when there is one. */
export const coverageKeyIdFor = (location, vendorId) =>
  String(location?.isOutlet ? location._id : vendorId);

/* ─────────────────────────────────────────────────────────────────────────────
 * ONE CARD PER VENDOR (4 Oct 2026)
 *
 * A chain used to fill the page: Yalelo's 19 shops were 20 near-identical
 * cards, one after another, and every other brand sat below them. Each vendor
 * now shows ONCE, opened on the location that serves the shopper best, with
 * the number of locations on the card. The storefront's location picker
 * (components/LocationPicker) lists them all and changes it.
 *
 * Built on the flattened location cards above rather than beside them, so a
 * card is still exactly one location: its distance, open badge, coverage
 * verdict and click all keep working untouched. Grouping only decides WHICH
 * of a vendor's locations stands for it.
 *
 * The same functions, with the same names and the same ranking, as the
 * mobile apps (Lusuku_Expo_Clean app/utility/vendorOutlets.js), so a shopper
 * is opened on the same branch on the website and on the phone.
 * ───────────────────────────────────────────────────────────────────────────── */

const isRecord = (value) => Boolean(value) && typeof value === "object";

/** The vendor a location card belongs to: an outlet's parent, or the row itself. */
export const vendorKeyOf = (card) =>
  String(card?.isOutlet ? card.parentVendorId : card?._id);

/**
 * Straight-line distance from the shopper's delivery point to a location, in
 * km, or null when either side has no coordinates. An estimate for ranking
 * and display only; the backend coverage verdict is authoritative wherever a
 * page has it. `calculateDistance` is injected (redux/userLocationSlice) so
 * this file stays free of the store.
 */
export const outletDistanceKm = (location, deliveryPoint, calculateDistance) => {
  if (
    !deliveryPoint ||
    !location?.latitude ||
    !location?.longitude ||
    typeof calculateDistance !== "function"
  ) {
    return null;
  }
  const km = calculateDistance(
    Number(deliveryPoint.latitude),
    Number(deliveryPoint.longitude),
    parseFloat(location.latitude),
    parseFloat(location.longitude)
  );
  return Number.isFinite(km) ? km : null;
};

/**
 * A location card with its distance from the delivery point (`distance`, a
 * fixed(1) string for display, and `distanceNum` for sorting). A card whose
 * distance cannot be worked out comes back untouched.
 */
export const withDistance = (location, deliveryPoint, calculateDistance) => {
  const km = outletDistanceKm(location, deliveryPoint, calculateDistance);
  if (km === null) return location;
  return { ...location, distance: km.toFixed(1), distanceNum: km };
};

/**
 * Does this location deliver to the shopper? true / false from the
 * straight-line distance against its delivery radius, null when either is
 * unknown. A page with the backend coverage verdict passes its own `reaches`
 * instead.
 */
export const locationReaches = (card) => {
  const km = Number(card?.distanceNum);
  const radius = Number(card?.deliveryZoneRadius);
  if (
    card?.distanceNum === null ||
    card?.distanceNum === undefined ||
    !Number.isFinite(km) ||
    !Number.isFinite(radius) ||
    radius <= 0
  ) {
    return null;
  }
  return km <= radius;
};

/*
 * How good a location is to open on, lower is better:
 *   0  open (or not known to be closed) and delivers to the shopper
 *   1  open, delivery not known (no location yet, or no radius)
 *   2  open, but out of reach
 *   3  closed
 * Ties go to the nearest, then to the vendor's own row (its main branch),
 * then to list order.
 */
const locationRank = (card, isOpen, reaches) => {
  const open = typeof isOpen === "function" ? isOpen(card) : null;
  const covers =
    typeof reaches === "function" ? reaches(card) : locationReaches(card);
  if (open === false) return 3;
  if (covers === true) return 0;
  if (covers === false) return 2;
  return 1;
};

const distanceOrLast = (card) => {
  const km = Number(card?.distanceNum);
  return card?.distanceNum === null ||
    card?.distanceNum === undefined ||
    !Number.isFinite(km)
    ? Number.POSITIVE_INFINITY
    : km;
};

/**
 * The best of one vendor's locations, by the ranking above.
 *
 * @param {object[]} locations  that vendor's location cards
 * @param {object}   [options]
 * @param {Function} [options.isOpen]   card => true | false | null (unknown)
 * @param {Function} [options.reaches]  card => true | false | null (unknown)
 */
export const preferredLocation = (locations, { isOpen, reaches } = {}) => {
  const list = (Array.isArray(locations) ? locations : []).filter(isRecord);
  if (list.length === 0) return null;
  return list
    .map((card, index) => ({
      card,
      index,
      rank: locationRank(card, isOpen, reaches),
      km: distanceOrLast(card),
    }))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        a.km - b.km ||
        Number(Boolean(a.card.isOutlet)) - Number(Boolean(b.card.isOutlet)) ||
        a.index - b.index
    )[0].card;
};

/**
 * One card per vendor, from a list of location cards.
 *
 * `cards` is what may be shown (already searched and filtered); `allCards` is
 * every location, so the count on the card is the vendor's whole estate even
 * when a search for "Bulaga" let only one of them through. The chosen card
 * gains `locationCount` (how many places the vendor can be ordered from) and
 * `vendorKey` (a stable React key); everything else is the location's own.
 * Vendors keep the order in which their first location appears.
 *
 * @param {object[]} cards
 * @param {object}   [options]
 * @param {object[]} [options.allCards=cards]
 * @param {Function} [options.keyOf=vendorKeyOf]
 * @param {Function} [options.isOpen]
 * @param {Function} [options.reaches]
 */
export const onePerVendor = (
  cards,
  { allCards, keyOf = vendorKeyOf, isOpen, reaches } = {}
) => {
  const shown = (Array.isArray(cards) ? cards : []).filter(isRecord);
  const every = Array.isArray(allCards) ? allCards.filter(isRecord) : shown;

  const counts = new Map();
  every.forEach((card) => {
    const key = keyOf(card);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const groups = new Map();
  shown.forEach((card) => {
    const key = keyOf(card);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(card);
  });

  return [...groups.entries()].map(([key, group]) => ({
    ...preferredLocation(group, { isOpen, reaches }),
    vendorKey: key,
    locationCount: Math.max(counts.get(key) || 0, group.length),
  }));
};

/** "20 locations" for a chain's card; null for a vendor with one place to order from. */
export const locationCountLabel = (count) => {
  const n = Number(count);
  return Number.isFinite(n) && n > 1 ? `${n} locations` : null;
};

/**
 * Every place a vendor can be ordered from, for the storefront's location
 * picker: its own row first (its main branch, `isOutlet: false`), then each
 * active outlet, each with its distance from the delivery point. The rows are
 * the listing's own (`flattenVendorLocations`), so the picker and the card
 * the shopper clicked agree on what a location carries; `outletRow` adds a
 * vertical's fields to every outlet, exactly as on the listing.
 */
export const vendorLocationsOf = (
  vendor,
  { deliveryPoint = null, calculateDistance, outletRow = null } = {}
) =>
  isRecord(vendor)
    ? flattenVendorLocations([vendor], { outletRow }).map((location) =>
        withDistance(location, deliveryPoint, calculateDistance)
      )
    : [];

/**
 * The picker's order: the best location first by the same ranking the
 * listing uses, then the rest nearest first, with locations of unknown
 * distance last in the order the vendor lists them.
 */
export const sortLocationsForPicker = (locations, { isOpen, reaches } = {}) => {
  const list = (Array.isArray(locations) ? locations : []).filter(isRecord);
  const best = preferredLocation(list, { isOpen, reaches });
  const rest = list
    .filter((card) => card !== best)
    .map((card, index) => ({ card, index, km: distanceOrLast(card) }))
    .sort((a, b) => a.km - b.km || a.index - b.index)
    .map(({ card }) => card);
  return best ? [best, ...rest] : rest;
};
